import PropTypes from 'prop-types';
import { useMemo } from 'react';
import DateInput from 'components/forms/dynamic-form-inputs/date-input';
import TextField from 'apps/interactions/form/fields/standard-fields/text-field';
import SwitchInput from 'components/forms/dynamic-form-inputs/switch-input';
import StatusField from 'apps/interactions/form/fields/standard-fields/status-field';
import SummaryField from 'apps/interactions/form/fields/standard-fields/summary-field';
import PurposeField from 'apps/interactions/form/fields/standard-fields/purpose-field';
import RelatedProposalField from 'apps/interactions/form/fields/standard-fields/related-proposal-field';
import CustomFields from 'components/forms/dynamic-form-inputs/custom-fields/custom-fields';
import SolicitorField from 'apps/interactions/form/fields/standard-fields/solicitor-field';
import SecondaryTargetField from 'apps/interactions/form/fields/standard-fields/secondary-target-field';
import InteractionTypeField from 'apps/interactions/form/fields/shared-fields/interaction-type-field';
import { DynamicField } from '@evertrue/sdk';
import { useInteractionFormContext } from 'apps/interactions/form/context/interaction-form-context';
import { shouldHideInteractionField } from 'apps/interactions/utils/interactions-utils';

const StandardBody = ({ values, contactId, referrer }) => {
  const { interactionStandardFieldsConfig } = useInteractionFormContext();
  const {
    labels = [],
    hasInteractionWrites,
    secondaryTargetLimit,
    interactionSolicitorLimit,
    enableInteractionSolicitorType,
    orgCustomFields,
    hasCustomFields,
  } = useInteractionFormContext();

  const hasStatus = labels.includes('status');
  const hidePurposeField = shouldHideInteractionField(interactionStandardFieldsConfig?.purpose);

  // Build contactIds array including primary contact and secondary targets
  const contactIds = useMemo(() => {
    const secondaryTargets = values.secondary_target || [];
    const ids = [contactId, ...secondaryTargets.map((t) => t.target_id || t.id || t.contact_id)].filter(
      (id) => id != null
    );
    return ids;
  }, [contactId, values.secondary_target]);

  return (
    <>
      {secondaryTargetLimit > 0 && (
        <DynamicField
          name="secondary_target"
          label={`Additional Constituents (${secondaryTargetLimit} maximum)`}
          values={values}
          as={SecondaryTargetField}
          contactId={contactId}
        />
      )}
      <div className="form-row">
        <DynamicField
          name="interaction_type"
          label="Interaction Type"
          values={{ ...values, has_interaction_writes: hasInteractionWrites }}
          as={InteractionTypeField}
        />
        <DynamicField name="substantive" label="Substantive" values={values} as={SwitchInput} />
      </div>
      <div className="form-row">
        <DynamicField
          name="date_occurred"
          label="Date"
          values={values}
          as={DateInput}
          triggerClassName="ignore-margin"
        />
        {/* If we ever get rid of this enabelInteractionSolicitorType flag in launchdarkly, we can clean this up and just render one version of the solicitor field. */}
        {!enableInteractionSolicitorType && (
          <DynamicField
            name="solicitor"
            label={interactionSolicitorLimit ? `Solicitor (${interactionSolicitorLimit} maximum)` : 'Solicitor'}
            values={values}
            as={SolicitorField}
            enableInteractionSolicitorType={enableInteractionSolicitorType}
          />
        )}
      </div>
      {enableInteractionSolicitorType && (
        <DynamicField
          name="solicitor"
          label={interactionSolicitorLimit ? `Solicitor (${interactionSolicitorLimit} maximum)` : 'Solicitor'}
          values={values}
          as={SolicitorField}
          enableInteractionSolicitorType={enableInteractionSolicitorType}
        />
      )}
      <DynamicField
        name="status"
        label="Status"
        values={{ ...values, has_status_labels: hasStatus }}
        as={StatusField}
      />
      <DynamicField name="summary" label="Summary" values={values} as={SummaryField} />
      <DynamicField name="text" label="Description" values={values} as={TextField} />
      {!hidePurposeField && <DynamicField name="purpose" label="Purpose" values={values} as={PurposeField} />}
      <DynamicField
        name="proposal_id"
        label="Related Proposal"
        values={values}
        as={RelatedProposalField}
        contactIds={contactIds}
        required={interactionStandardFieldsConfig?.related_proposal?.required}
        disabled={referrer === 'proposal' && !!values.proposal_id}
      />
      {/* NOTE: Custom Fields are handled differently -- they use the "component" prop, not the "as" prop. See the difference on Formik's docs */}
      <DynamicField
        name="custom_field"
        values={values}
        component={CustomFields}
        wrap={false}
        orgCustomFields={orgCustomFields}
        hasCustomFields={hasCustomFields}
      />
    </>
  );
};

StandardBody.propTypes = {
  values: PropTypes.object,
  contactId: PropTypes.number,
  referrer: PropTypes.string,
};

export default StandardBody;
